import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Clock, Flame, ArrowRight, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePricing } from '../utils/PricingContext';
import { getDiscountTimeRemaining } from '../utils/pricing';

/**
 * Floating upgrade banner - uses PricingContext, NO HARDCODED PRICES
 */
const UpgradeBanner = ({
  userTier = 'free',
  onDismiss,
  variant = 'bottom'
}) => {
  const navigate = useNavigate();
  const { getStarterPlan, formatPrice, loading } = usePricing();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState({ hours: 23, minutes: 59, seconds: 59, expired: false });

  const starterPlan = getStarterPlan();

  useEffect(() => {
    const lastDismissed = localStorage.getItem('upgrade_banner_dismissed');
    if (lastDismissed) {
      const dismissedTime = parseInt(lastDismissed);
      const hoursSinceDismiss = (Date.now() - dismissedTime) / (1000 * 60 * 60);
      if (hoursSinceDismiss < 4) return;
    }

    if (userTier === 'free') {
      const timer = setTimeout(() => setIsVisible(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [userTier]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(getDiscountTimeRemaining());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('upgrade_banner_dismissed', Date.now().toString());
    setTimeout(() => setIsVisible(false), 300);
    onDismiss?.();
  };

  const handleUpgrade = () => {
    navigate('/subscribe');
  };

  if (!isVisible || isDismissed || userTier !== 'free') return null;

  // Get pricing from context
  const starterPrice = starterPlan ? formatPrice(starterPlan.price, starterPlan.currency) : '...';
  const starterOriginal = starterPlan ? formatPrice(starterPlan.original_price, starterPlan.currency) : '...';
  const starterDuration = starterPlan?.duration_months || 1;
  const discountPercent = starterPlan?.discount_percent || 50;

  if (variant === 'corner') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, x: 100, y: 100 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: 100 }}
          className="fixed bottom-6 right-6 z-50 max-w-sm"
        >
          <div className="bg-gradient-to-br from-[#0F131F] to-[#1a1f2e] border border-emerald-500/30 rounded-2xl p-6 shadow-2xl shadow-emerald-900/20">
            <button onClick={handleDismiss} className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-lg">
                <Gift className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-bold font-mono text-sm">EXCLUSIVE OFFER</p>
                <p className="text-emerald-400 text-xs font-mono">Just for you!</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs line-through">{starterOriginal}</p>
                  <p className="text-2xl font-bold text-white">{starterPrice}<span className="text-sm text-emerald-400"> for {starterDuration} month</span></p>
                </div>
                <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  -{discountPercent}%
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-2 mb-4 text-orange-400">
              <Clock className="w-4 h-4" />
              <span className="font-mono text-sm">
                {String(timeRemaining.hours).padStart(2, '0')}:{String(timeRemaining.minutes).padStart(2, '0')}:{String(timeRemaining.seconds).padStart(2, '0')}
              </span>
            </div>

            <button
              onClick={handleUpgrade}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl flex items-center justify-center space-x-2 hover:scale-105 transition-transform"
            >
              <Zap className="w-5 h-5" />
              <span className="font-mono">CLAIM OFFER</span>
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Bottom banner (default)
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-0 left-0 right-0 z-50"
      >
        <div className="bg-gradient-to-r from-[#0F131F] via-[#1a1f2e] to-[#0F131F] border-t border-emerald-500/30 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="hidden sm:flex items-center space-x-2">
                  <Flame className="w-6 h-6 text-orange-500 animate-pulse" />
                  <div className="bg-red-500/20 border border-red-500/30 px-3 py-1 rounded-full">
                    <span className="text-red-400 font-mono text-sm font-bold">LIMITED TIME</span>
                  </div>
                </div>
                <div>
                  <p className="text-white font-bold font-mono text-sm md:text-base">
                    🔥 Get <span className="text-emerald-400">{discountPercent}% OFF</span> - Unlock Premium Features!
                  </p>
                  <p className="text-slate-400 text-xs md:text-sm font-mono">
                    AI Career Roadmaps, Resume Analysis & More
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4 bg-black/30 px-4 py-2 rounded-lg border border-white/10">
                <Clock className="w-5 h-5 text-orange-400" />
                <div className="flex space-x-1 font-mono text-white">
                  <div className="bg-black/50 px-2 py-1 rounded text-lg font-bold">
                    {String(timeRemaining.hours).padStart(2, '0')}
                  </div>
                  <span className="text-orange-400">:</span>
                  <div className="bg-black/50 px-2 py-1 rounded text-lg font-bold">
                    {String(timeRemaining.minutes).padStart(2, '0')}
                  </div>
                  <span className="text-orange-400">:</span>
                  <div className="bg-black/50 px-2 py-1 rounded text-lg font-bold">
                    {String(timeRemaining.seconds).padStart(2, '0')}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="text-right hidden lg:block">
                  <p className="text-slate-500 text-xs line-through">{starterOriginal}</p>
                  <p className="text-white font-bold text-xl">{starterPrice}<span className="text-sm text-emerald-400"> • {starterDuration} month</span></p>
                </div>
                <button
                  onClick={handleUpgrade}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl flex items-center space-x-2 hover:scale-105 transition-transform shadow-lg shadow-emerald-500/20"
                >
                  <Zap className="w-5 h-5" />
                  <span className="font-mono text-sm">UPGRADE NOW</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={handleDismiss} className="p-2 text-slate-500 hover:text-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpgradeBanner;
